import { z } from 'zod';
import { DEFAULT_BOOK } from '../../config.ts';

const EXTERNAL_ID_MAX_LENGTH = 32;

const EXTERNAL_ID_PATTERN = /^#?[A-Za-z0-9][A-Za-z0-9/-]*$/;

export function normaliseExternalId(raw: string): string | null {
  const id = raw.trim();
  return id.length <= EXTERNAL_ID_MAX_LENGTH && EXTERNAL_ID_PATTERN.test(id) ? id : null;
}

/** Mirrored in SQL as `lower(ltrim(external_id, '#'))` — change both. */
export function externalIdKey(externalId: string): string {
  return externalId.replace(/^#+/, '').toLowerCase();
}

/** Written `book:87`; `:` because ids may contain `/` and `-`. No book means the default book. */
export interface RecommendationRef {
  book: string;
  externalId: string;
}

export interface Labelled {
  externalId: string | null;
  book: string;
  trackingTradingsymbol: string;
}

export function formatRecommendationRef(ref: RecommendationRef): string {
  const id = ref.externalId.replace(/^#/, '');
  return ref.book === DEFAULT_BOOK ? `#${id}` : `#${ref.book}:${id}`;
}

export function externalIdTakenMessage(
  ref: RecommendationRef,
  clash: { underlying: string; recommendedOn: string },
): string {
  return (
    `reference ${formatRecommendationRef(ref)} is already taken in the ${ref.book} book by ` +
    `${clash.underlying} (${clash.recommendedOn})`
  );
}

export const EXTERNAL_ID_SPELLING = `1–${EXTERNAL_ID_MAX_LENGTH} letters, digits, hyphens or slashes, e.g. 87 or ZB-87`;

const EXTERNAL_ID_RULE = `reference must be ${EXTERNAL_ID_SPELLING}`;

export const externalIdSchema = z
  .string()
  .transform(normaliseExternalId)
  .refine((id): id is string => id !== null, { message: EXTERNAL_ID_RULE });

/** Never name by the bare underlying: "NIFTY · LONG" misreads a 24500 call as a long on the index. */
export function recommendationLabel(recommendation: Labelled): string {
  return recommendation.externalId === null
    ? recommendation.trackingTradingsymbol
    : `${formatRecommendationRef({ book: recommendation.book, externalId: recommendation.externalId })} ${recommendation.trackingTradingsymbol}`;
}
